import {
  Button,
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AddressCard, Input } from '@shared/components';
import { links } from '@shared/utils';
import { Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useGetAllOnboardingInfo } from '../../hooks';
import {
  IOnboardingDataCompanyForm,
  OnboardingDataCompanySchema,
} from '../../schemas';
import { getAddressByPostalCode } from '../../services';
import { useDataState } from '../../store';

export const DataCompanyForm = ({ rootEntity }: { rootEntity: string }) => {
  const { data: onboardingInfo } = useGetAllOnboardingInfo();
  const { t } = useTranslation(rootEntity);
  const navigate = useNavigate();

  const [postalCodePartial, setPostalCodePartial] = useState(false);
  const { setOnboardingDataCompanyForm } = useDataState();
  const hookForm = useForm<IOnboardingDataCompanyForm>({
    resolver: zodResolver(OnboardingDataCompanySchema()),
    shouldFocusError: false,
    defaultValues: {
      document: onboardingInfo?.requestCustomers[0].documentNumber || '',
      name:
        `${onboardingInfo?.requestCustomers[0].firstName} ${onboardingInfo?.requestCustomers[0].lastName}` ||
        '',
      phone: onboardingInfo?.requestCustomers[0].phoneNumber || '',
      email: onboardingInfo?.requestCustomers[0].email || '',
    },
  });

  const handleSubmit = (data: IOnboardingDataCompanyForm) => {
    setOnboardingDataCompanyForm(data);
    navigate(links.onboarding.addResponsible);
  };

  const handleGetAddress = async () => {
    const postalCode = hookForm.getValues('postalCode');
    if (postalCode.length === 9) {
      const response = await getAddressByPostalCode(postalCode);

      if (response.erro) {
        hookForm.setError('postalCode', {
          message: t('postalCodeError'),
        });
      } else {
        hookForm.clearErrors('postalCode');
      }

      if (response.logradouro === '' || response.bairro === '') {
        setPostalCodePartial(true);
      } else {
        setPostalCodePartial(false);
      }

      hookForm.setValue('street', response.logradouro);
      hookForm.setValue('district', response.bairro);
      hookForm.setValue('city', response.localidade);
      hookForm.setValue('state', response.uf);
    }
  };

  const handleClearPostalCode = () => {
    hookForm.setValue('postalCode', '');
    hookForm.setValue('street', '');
    hookForm.setValue('district', '');
    hookForm.setValue('city', '');
    hookForm.setValue('state', '');
    hookForm.setValue('number', '');
    hookForm.setValue('complement', '');
  };

  useEffect(() => {
    handleGetAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookForm.watch('postalCode')]);

  return (
    <Flex flexDir="column" gap={4}>
      <FormProvider {...hookForm}>
        <form onSubmit={hookForm.handleSubmit(handleSubmit)}>
          <Flex gap={4} h="100dvh" maxH="calc(100dvh - 242px)" overflowY="auto">
            <Flex flexDir="column" gap={6} w="full">
              <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                {t('companyData')}
              </Text>

              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={2}>
                  <Input
                    label={t('companyDocument')}
                    name="document"
                    placeholder={t('companyDocumentPlaceholder')}
                    type="cnpj"
                  />
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('companyName')}
                    name="name"
                    placeholder={t('companyNamePlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={4}>
                  <Input
                    label={t('companyFantasyName')}
                    name="fantasyName"
                    placeholder={t('companyFantasyNamePlaceholder')}
                  />
                </GridItem>
              </Grid>

              <Flex flexDir="column">
                <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                  {t('billingAddress')}
                </Text>
              </Flex>

              <Grid templateColumns="repeat(6, 1fr)" gap={4} w="full">
                <GridItem
                  colSpan={{ base: 2, md: 2 }}
                  display="flex"
                  gap={2}
                  alignItems="end"
                >
                  <Input
                    label={t('postalCode')}
                    name="postalCode"
                    type="mask"
                    mask="99999-999"
                    placeholder={t('postalCodePlaceholder')}
                  />
                  <IconButton
                    aria-label="Buscar endereço"
                    icon={<Icon as={Trash} />}
                    onClick={handleClearPostalCode}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 2, md: 2 }}>
                  <Input
                    label={t('number')}
                    name="number"
                    placeholder={t('numberPlaceholder')}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 6, md: 2 }}>
                  <Input
                    label={t('complement')}
                    name="complement"
                    placeholder={t('complementPlaceholder')}
                  />
                </GridItem>

                {postalCodePartial && (
                  <>
                    <GridItem colSpan={{ base: 6, md: 3 }}>
                      <Input
                        label={t('street')}
                        name="street"
                        placeholder={t('streetPlaceholder')}
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 6, md: 3 }}>
                      <Input
                        label={t('district')}
                        name="district"
                        placeholder={t('districtPlaceholder')}
                      />
                    </GridItem>
                  </>
                )}
              </Grid>

              {/* caso não haja erro no campo postalCode, exibe o endereço */}
              {hookForm.getValues('city') && (
                <AddressCard
                  city={hookForm.watch('city')}
                  state={hookForm.watch('state')}
                  street={hookForm.watch('street')}
                  district={hookForm.watch('district')}
                  number={hookForm.watch('number')}
                  complement={hookForm.watch('complement')}
                />
              )}

              <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                <GridItem colSpan={4}>
                  <Text color="brand.800" fontSize="2xl" fontWeight={600}>
                    {t('companyContact')}
                  </Text>
                </GridItem>

                <GridItem colSpan={2}>
                  <Input
                    label={t('phone')}
                    name="phone"
                    placeholder={t('phonePlaceholder')}
                    type="phone"
                  />
                </GridItem>
              </Grid>
            </Flex>
          </Flex>

          <Flex justifyContent="end">
            <Button type="submit" colorScheme="brand" w={240}>
              {t('common:continue')}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
